/**
 * @file planCompare.ts
 * @brief The rows Free, Pro and Business show side by side on Super Admin →
 *        Plans & seats: same rows, same order, on every card (W18)
 *
 * @details
 * DES 24-10-2026: the cards were hard to compare (Pro and Business did not
 * say Meeting Notes is included). Every card now lists the same "What's
 * included" rows and the same "Pay as you go" rows per currency; where a plan
 * does not have something the row says so ("Not included", "None") instead of
 * leaving it out. Display only: values come from the setting the row edits
 * (`key`, same form value as the side sheet) or from the GET /api/admin/plans
 * payload (`included`, `rates`). A value the payload does not carry yet (the
 * BACKEND W18 deploy) shows "–", never a guess.
 *
 * What billing does, and so what the rows say:
 *  - Meeting Notes on Pro / Business is unlimited under NOTES_FAIR_USE_MINUTES
 *    (per person, per plan month) only while NOTES_POOL_ENABLED is on; while
 *    it is off, plan Notes and voice notes use the Intelligence minutes.
 *  - Free voice notes share FREE_PLAN_NOTES_MINUTES.
 *  - The interpretation minimum is one platform value (MIN_CHARGE_<CUR>_MINOR,
 *    Customer rates), the same on every plan, and is not charged on a session
 *    that included or free minutes touched.
 *  - Pro / Business have no per-meeting Intelligence minimum: extra minutes go
 *    on the next invoice.
 * Realtime interpretation has a rate but no way in: the phone's
 * `featureFlags.realtimeBeta` is off and the web has no Realtime start.
 *
 * B20 / W17 (24-10-2026): the new plan numbers take their place in these
 * rows when the backend sends them: Free "300 min, once" / "600 min / month"
 * by the reset mode and an "Expires" row in one-time mode; Pro / Business
 * Meeting Notes minutes (-1 = unlimited under fair use), their own fair use
 * (0 = the shared one), history days (0 = unlimited), the interpretation and
 * realtime minimum per plan and currency, the Business maximum seats. An old
 * backend without them keeps the W18 rows.
 *
 * A setting this file does not place (a later addition) is appended after
 * the fixed rows, so it is never dropped.
 *
 * Related: components/SubscriptionPlansCard.tsx, launchPlans.ts,
 *   BACKEND src/features/admin/planCards.mjs
 *
 * @author DES (prepared with Claude)
 * @date 24-10-2026
 */

import {
  FREE_EXPIRY_KEY,
  FREE_RESET_MODE_KEY,
  freeModeOf,
  minorRowText,
  noteMinKey,
  noteRateKey,
  planRowGroup,
  rowLabel,
  rowValue,
  type PlanCurrency,
  type PlanSetting,
} from "./launchPlans.ts";
import type { PlanCard } from "./schemas.ts";
import {
  FREE_POOL_LABEL,
  FREE_POOL_SUB,
  FROM_FREE_MINUTES,
  freeIntelValue,
  freePoolValue,
} from "../../shared/billing/freePool.ts";

/** One line of a plan card. Rows with a `key` edit that setting in place. */
export interface CompareRow {
  /** Same on every card, so the rows line up. */
  id: string;
  label: string;
  /** The setting this row edits, when one exists for this plan. */
  setting?: PlanSetting;
  /** What the row shows on the right. */
  value: string;
  /** Read-only rows: where the value comes from / where to change it. */
  tip?: string;
  /** W34: a small line under the label (Free's one pool). */
  sub?: string;
  /** W34: a per-use price Free never charges (B43); shown greyed, read-only. */
  unused?: boolean;
}

export interface CompareContext {
  /** Any setting by key: this card's, "All plans", or a page switch. */
  setting: (key: string) => PlanSetting | undefined;
  /** The form's current display value ("14.99", "60", "true"). */
  value: (key: string) => string;
}

export const NOT_LOADED = "–";

const whole = (n: number) => n.toLocaleString("en-GB");

/** Plans that get the compare rows (Enterprise is set per company). */
export function hasCompareRows(card: Pick<PlanCard, "code">): boolean {
  return card.code === "free" || card.code === "pro" || card.code === "business";
}

/** The page's Notes pool switch, or the payload's copy when the switch didn't load. */
function notesPoolOn(card: PlanCard, ctx: CompareContext): boolean | null {
  if (ctx.setting("NOTES_POOL_ENABLED")) return ctx.value("NOTES_POOL_ENABLED") === "true";
  return card.included?.notesPoolOn ?? null;
}

function editRow(
  id: string,
  label: string,
  key: string,
  ctx: CompareContext,
  text?: (s: PlanSetting, display: string) => string,
): CompareRow | null {
  const s = ctx.setting(key);
  if (!s) return null;
  const display = ctx.value(key);
  return { id, label, setting: s, value: text ? text(s, display) : rowValue(s, display) };
}

const fixed = (id: string, label: string, value: string, tip?: string): CompareRow => ({
  id,
  label,
  value,
  tip,
});

/** Free's reset mode in the form ("monthly" on a backend without it). */
export function freeMode(ctx: CompareContext) {
  return freeModeOf(ctx.setting(FREE_RESET_MODE_KEY) ? ctx.value(FREE_RESET_MODE_KEY) : "monthly");
}

/** "300 min, once" / "600 min / month"; "10, once" / "10 / month". */
function freeAmount(display: string, unit: string, once: boolean): string {
  const n = Number(display.trim());
  if (display.trim() === "" || !Number.isFinite(n)) return "Not set";
  return once ? `${whole(n)}${unit}, once` : `${whole(n)}${unit} / month`;
}

/** Hours from the form's hours value ("60"), or null. */
function hoursOf(display: string): number | null {
  const h = Number(display.trim());
  return display.trim() === "" || !Number.isFinite(h) ? null : h;
}

const POOL_OFF_TIP =
  "The separate Meeting Notes pool is off (Switches): Notes minutes count against the plan's Intelligence minutes.";

/** "What's included": the same seven rows on Free, Pro and Business. */
export function includedRows(card: PlanCard, ctx: CompareContext): CompareRow[] {
  const code = card.code;
  const free = code === "free";
  const business = code === "business";
  const pool = notesPoolOn(card, ctx);
  const inc = card.included;
  const rows: (CompareRow | null)[] = [];

  const once = free && freeMode(ctx) === "one_time";
  const P = `PLAN_${code.toUpperCase()}`;
  // B20 backend: per-plan Notes numbers exist (an old backend has neither).
  const b20 = !!ctx.setting("PLAN_PRO_NOTES_MINUTES");
  const sharedFairUse = hoursOf(ctx.value("NOTES_FAIR_USE_MINUTES"));
  /** This plan's fair use in hours: its own, or the shared one when 0. */
  const planFairUse = (): number | null => {
    const own = hoursOf(ctx.value(`${P}_NOTES_FAIR_USE_MINUTES`));
    return own && own > 0 ? own : sharedFairUse;
  };
  const per = business ? "seat / " : "";

  // Meeting Notes. W34 (DES 31-10-2026): Free's minutes are one pool for
  // everything (B43), so the pool is its own first row and each product says
  // where its minutes come from.
  if (free) {
    const pool = editRow("freeMinutes", FREE_POOL_LABEL, "FREE_PLAN_NOTES_MINUTES", ctx, (_s, d) =>
      d.trim() === "" ? "Not set" : (freePoolValue(Number(d.trim()), once) ?? "Not set"),
    );
    if (pool) rows.push({ ...pool, sub: FREE_POOL_SUB });
    rows.push(fixed("notes", "Meeting Notes", FROM_FREE_MINUTES, "Uses the Free minutes above."));
  } else if (pool === false) {
    rows.push(
      ctx.setting(`${P}_NOTES_MINUTES`)
        ? editRow("notes", "Meeting Notes", `${P}_NOTES_MINUTES`, ctx, () => "Uses Intelligence minutes")
        : fixed("notes", "Meeting Notes", "Uses Intelligence minutes", POOL_OFF_TIP),
    );
  } else if (b20 && ctx.setting(`${P}_NOTES_MINUTES`)) {
    rows.push(
      editRow("notes", "Meeting Notes", `${P}_NOTES_MINUTES`, ctx, (s, d) => {
        if (d.trim() !== "-1") return rowValue(s, d);
        const h = planFairUse();
        return h == null ? "Unlimited, fair use" : `Unlimited, fair use ${whole(h)} h / ${per}month`;
      }),
    );
  } else {
    rows.push(
      editRow("notes", "Meeting Notes", "NOTES_FAIR_USE_MINUTES", ctx, (_s, d) => {
        const h = Number(d);
        if (d.trim() === "" || !Number.isFinite(h)) return NOT_LOADED;
        return `Unlimited, fair use ${whole(h)} h / ${business ? "seat / " : ""}month`;
      }) ?? fixed("notes", "Meeting Notes", NOT_LOADED, "Fair use not loaded."),
    );
  }

  // Meeting Notes fair use, per plan (B20). Free: the minutes are the limit.
  if (b20) {
    if (free) {
      rows.push(fixed("fairUse", "Notes fair use", "Not needed", "Free has a minutes allowance, not a fair use."));
    } else {
      rows.push(
        editRow("fairUse", "Notes fair use", `${P}_NOTES_FAIR_USE_MINUTES`, ctx, (s, d) => {
          const own = hoursOf(d);
          if (own === null) return rowValue(s, d);
          if (own > 0) return rowValue(s, d);
          return sharedFairUse == null ? "Shared" : `Shared, ${whole(sharedFairUse)} h / ${per}month`;
        }),
      );
    }
  }

  // Meeting Intelligence. B43 (31-10-2026, ADR-061): Free has ONE pool for
  // Notes or Full Intelligence, never charged; FREE_INTEL_MAX_MINUTES caps
  // the Full part (the pool or more = no cap). A backend before B43 has no key.
  if (free) {
    const pool = Number(ctx.value("FREE_PLAN_NOTES_MINUTES"));
    rows.push(
      editRow("intelligence", "Meeting Intelligence", "FREE_INTEL_MAX_MINUTES", ctx, (_s, d) =>
        freeIntelValue(d.trim() === "" ? null : Number(d.trim()), pool),
      ) ??
        fixed(
          "intelligence",
          "Meeting Intelligence",
          "Pay as you go, card needed",
          "No minutes included. Charged per minute at the no-plan rate below.",
        ),
    );
    const grace = editRow("grace", "After the free minutes", "FREE_GRACE_MINUTES", ctx, (_s, d) => {
      const n = Number(d.trim());
      return d.trim() === "" || !Number.isFinite(n) ? "Not set" : `Recording stops after ${whole(n)} min`;
    });
    if (grace) rows.push({ ...grace, tip: "A live Free recording keeps going this long after the free minutes, then stops by itself. Never charged." });
  } else if (business) {
    rows.push(
      editRow("intelligence", "Meeting Intelligence", "PLAN_BUSINESS_SOFT_CAP_MINUTES", ctx, (s, d) => {
        const v = rowValue(s, d);
        return v === "Not set" ? v : `${v}, pooled`;
      }),
    );
  } else {
    rows.push(editRow("intelligence", "Meeting Intelligence", "PLAN_PRO_INCLUDED_MINUTES", ctx));
  }

  // Ask questions
  rows.push(
    free
      ? editRow("ask", "Ask AI questions", "FREE_PLAN_ASK_LIMIT", ctx, (_s, d) => freeAmount(d, "", once))
      : editRow("ask", "Ask AI questions", `${P}_SEARCH_LIMIT`, ctx),
  );

  // Voice notes
  if (free) {
    rows.push(
      fixed("voice", "Voice notes", FROM_FREE_MINUTES, "Voice notes, Meeting Notes and Full Intelligence share the Free minutes."),
    );
  } else if (pool === null) {
    rows.push(fixed("voice", "Voice notes", NOT_LOADED, "Notes pool switch not loaded."));
  } else {
    rows.push(
      pool
        ? fixed("voice", "Voice notes", "Included", "Unlimited, in the Meeting Notes fair use.")
        : fixed("voice", "Voice notes", "Uses Intelligence minutes", POOL_OFF_TIP),
    );
  }

  // History
  if (free) {
    rows.push(editRow("history", "History", "FREE_PLAN_HISTORY_DAYS", ctx));
  } else if (ctx.setting(`${P}_HISTORY_DAYS`)) {
    rows.push(editRow("history", "History", `${P}_HISTORY_DAYS`, ctx));
  } else {
    const h = inc ? inc.historyDays : undefined;
    rows.push(
      fixed(
        "history",
        "History",
        h === undefined ? NOT_LOADED : h === null ? "Unlimited" : `${whole(h)} days`,
        "Set in code for paid plans.",
      ),
    );
  }

  // Interpretation included
  if (free) {
    rows.push(
      editRow("interpretation", "Interpretation included", "FREE_INTERPRET_MINUTES", ctx, (s, d) =>
        ctx.value("FREE_INTERPRET_ENABLED") === "false" ? "None (switched off)" : rowValue(s, d),
      ),
    );
  } else {
    const m = inc?.interpretationMinutes;
    rows.push(
      fixed(
        "interpretation",
        "Interpretation included",
        m === undefined ? NOT_LOADED : m > 0 ? `${whole(m)} min / month` : "None",
        "Paid plans pay per minute at the rates below.",
      ),
    );
  }

  // Seats
  if (business) {
    rows.push(
      editRow("seats", "Seats", "PLAN_BUSINESS_MIN_SEATS", ctx, (_s, d) =>
        d.trim() === "" ? "Not set" : `Minimum ${d.trim()}`,
      ),
    );
  } else {
    rows.push(fixed("seats", "Seats", "1", free ? "One person." : "One person per plan."));
  }

  // Last, so the rows above still line up across the cards (B20).
  if (business) rows.push(editRow("maxSeats", "Maximum seats", "PLAN_BUSINESS_MAX_SEATS", ctx));
  if (once) rows.push(editRow("expires", "Expires", FREE_EXPIRY_KEY, ctx));

  const out = rows.filter((r): r is CompareRow => r !== null);
  return [...out, ...leftOver(card, out, ctx, (s) => planRowGroup(s) === "included")];
}

/** Settings of this card the fixed rows did not place, in payload order. */
function leftOver(
  card: PlanCard,
  placed: CompareRow[],
  ctx: CompareContext,
  want: (s: PlanSetting) => boolean,
): CompareRow[] {
  const used = new Set(placed.map((r) => r.setting?.key).filter(Boolean));
  return card.settings
    .filter((s) => want(s) && !used.has(s.key))
    .map((s) => ({ id: s.key, label: rowLabel(s), setting: s, value: rowValue(s, ctx.value(s.key)) }));
}

/**
 * W34 (31-10-2026): B43 made Free never charged per use, so while Free is on
 * the no-plan Intelligence and Notes prices are not used for a personal
 * account. BACKEND still reads the keys when Free is off and for a
 * company-billed recording with no company rate, so the keys stay and are
 * edited where they always were; only the Free card's rows say "Not used".
 */
export const NOT_USED_ON_FREE = "Not used on Free";

/** Free switched on in the form (an old backend without the switch: no). */
export function freeOn(ctx: CompareContext): boolean {
  return ctx.value("FREE_PLAN_ENABLED") === "true";
}

function notUsedOnFree(row: CompareRow, editWhere: string): CompareRow {
  const was = row.value === NOT_LOADED ? "" : ` (${row.value})`;
  return {
    id: row.id,
    label: row.label,
    setting: row.setting,
    value: NOT_USED_ON_FREE,
    unused: true,
    tip:
      `Free is never charged (B43). This no-plan price${was} applies only if Free is switched off, ` +
      `or to a company-billed recording with no company rate. Change it ${editWhere}.`,
  };
}

const MIN_INTERPRETATION_TIP =
  "The platform minimum (Customer rates). Not charged when included or free minutes covered part of the session, or on a rejoined booking.";

/** "Pay as you go" for one currency: the same rows on Free, Pro and Business. */
export function paygRows(card: PlanCard, cur: PlanCurrency, ctx: CompareContext): CompareRow[] {
  const free = card.code === "free";
  const P = `PLAN_${card.code.toUpperCase()}`;
  const r = card.rates?.[cur];
  const money = (minor: number | null | undefined, unit: "min" | "meeting" | "session" | "recording") =>
    minor == null ? NOT_LOADED : minorRowText(minor, cur, unit);
  const rows: (CompareRow | null)[] = [];

  if (free) {
    rows.push(fixed("interp", "Interpretation", money(r?.interpretationMinor, "min"), "List rate. Change it on Customer rates."));
    rows.push(
      fixed(
        "realtime",
        "Realtime interpretation (hidden in the apps)",
        money(r?.realtimeMinor, "min"),
        "List rate. Change it on Customer rates. No app can start Realtime today.",
      ),
    );
    rows.push(
      editRow("extra", "Extra Intelligence", `RATE_BOARDROOM_PER_MINUTE_${cur}_MINOR`, ctx) ??
        fixed("extra", "Extra Intelligence", money(r?.extraIntelligenceMinor, "min"), "No-plan rate."),
    );
    rows.push(
      editRow("minMeeting", "Minimum per Intelligence meeting", `MIN_CHARGE_BOARDROOM_${cur}_MINOR`, ctx) ??
        fixed("minMeeting", "Minimum per Intelligence meeting", money(r?.minChargeMinor, "meeting"), "No-plan minimum."),
    );
  } else {
    rows.push(editRow("interp", "Interpretation", `${P}_S2S_RATE_${cur}_MINOR`, ctx));
    rows.push(
      editRow("realtime", "Realtime interpretation (hidden in the apps)", `${P}_REALTIME_RATE_${cur}_MINOR`, ctx),
    );
    rows.push(editRow("extra", "Extra Intelligence", `${P}_BOARDROOM_RATE_${cur}_MINOR`, ctx));
    rows.push(
      fixed("minMeeting", "Minimum per Intelligence meeting", "None", "Extra minutes go on the next invoice; no per-meeting minimum."),
    );
  }
  // W19 (B23): Meeting Notes after the free minutes, per minute + a minimum
  // per recording, for no plan only; on a plan Notes are never billed per
  // minute (the plan's Notes minutes / fair use).
  if (free) {
    rows.push(
      editRow("notesPayg", "Meeting Notes (after the free minutes)", noteRateKey(cur), ctx) ??
        fixed("notesPayg", "Meeting Notes (after the free minutes)", money(r?.noteMinor, "min"), "No-plan Notes rate (Customer rates). B43: never charged on Free, only while Free is off."),
    );
    rows.push(
      editRow("minNotes", "Minimum per Notes recording", noteMinKey(cur), ctx) ??
        fixed("minNotes", "Minimum per Notes recording", money(r?.noteMinChargeMinor, "recording"), "No-plan Notes minimum (Customer rates)."),
    );
  } else {
    rows.push(fixed("notesPayg", "Meeting Notes per minute", "Not charged", "On a plan, Meeting Notes use the plan's Notes minutes and fair use."));
    rows.push(fixed("minNotes", "Minimum per Notes recording", "None", "Plan Notes are never billed per recording."));
  }
  // B20: Pro / Business have their own minimum per currency; Free (no plan)
  // pays the platform one. A company's own minimum still wins.
  rows.push(
    (!free && editRow("minInterp", "Minimum per interpretation session", `${P}_MIN_CHARGE_${cur}_MINOR`, ctx)) ||
      fixed("minInterp", "Minimum per interpretation session", money(r?.interpretationMinMinor, "session"), MIN_INTERPRETATION_TIP),
  );
  rows.push(
    (!free &&
      editRow("minRealtime", "Minimum per realtime session", `${P}_MIN_CHARGE_REALTIME_${cur}_MINOR`, ctx)) ||
      fixed("minRealtime", "Minimum per realtime session", money(r?.realtimeMinMinor, "session"), MIN_INTERPRETATION_TIP),
  );

  let out = rows.filter((x): x is CompareRow => x !== null);
  // W34: the interpretation rows stay (the only per-use charge on Free).
  if (free && freeOn(ctx)) {
    const where: Record<string, string> = {
      extra: "under All plans → No plan",
      minMeeting: "under All plans → No plan",
      notesPayg: "on Customer rates",
      minNotes: "on Customer rates",
    };
    out = out.map((r) => (where[r.id] ? notUsedOnFree(r, where[r.id]) : r));
  }
  return [...out, ...leftOver(card, out, ctx, (s) => planRowGroup(s) === "payg" && s.currency === cur)];
}
